/**
 * Consumo estimado por nó a partir das edificações — candidato para a engenharia (US-3603) — Nexora Grid (frontend).
 *
 * Criado em: 26/10/2026 · Última modificação: 26/10/2026 · Versão: 1.0.0
 *
 * Por setor: estimado (edificações × parâmetros) × modelo (demanda de base do INP vigente) × medido (telemetria), com as
 * limitações escritas. A engenharia (`pode_editar`) ajusta os parâmetros do método e calcula; o resultado é um CANDIDATO
 * (nunca altera o modelo oficial) e baixa em CSV por nó.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, baixar, ErroApi } from "../api";

type Parametros = { per_capita_l_hab_dia: number | null; habitantes_por_domicilio: number; area_por_domicilio_m2: number;
  comercial_l_m2_dia: number; servico_l_m2_dia: number; desconhecido_como_residencial: boolean };
type Setor = { setor: string; nos: number; edificacoes: number; estimado_l_s: number; modelo_l_s: number; medido_l_s: number | null;
  estimado_sobre_modelo: number | null; estimado_sobre_medido: number | null };
type Estimativa = { criado_em: string; criado_por: string; per_capita_l_hab_dia: number; per_capita_origem: string; setores: Setor[];
  total_estimado_l_s: number; total_modelo_l_s: number; edificacoes_sem_no: number; limitacoes: string[] };
const CAMPOS: [keyof Parametros, string][] = [["per_capita_l_hab_dia", "Per capita (l/hab/dia; vazio = oficial SNIS/SINISA)"],
  ["habitantes_por_domicilio", "Habitantes por domicílio"], ["area_por_domicilio_m2", "Área construída por domicílio (m²)"],
  ["comercial_l_m2_dia", "Comercial (l/m²/dia)"], ["servico_l_m2_dia", "Serviço (l/m²/dia)"]];
const n = (v: number | null | undefined, casas = 2) => (v === null || v === undefined ? "—" : v.toLocaleString("pt-BR", { maximumFractionDigits: casas }));

export function ConsumoEstimado({ empresa, municipio }: { empresa: string; municipio: string }) {
  const [param, setParam] = useState<Parametros | null>(null);
  const [podeEditar, setPodeEditar] = useState(false);
  const [est, setEst] = useState<Estimativa | null>(null);
  const [motivo, setMotivo] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);
  const consulta = `empresa=${empresa}&municipio=${encodeURIComponent(municipio)}`;
  const carregar = useCallback(() => {
    if (!municipio) return;
    api<{ parametros: Parametros; pode_editar: boolean }>(`/v1/edificacoes/consumo/parametros?${consulta}`)
      .then((r) => { setParam(r.parametros); setPodeEditar(r.pode_editar); }).catch(() => setParam(null));
    api<Estimativa>(`/v1/edificacoes/consumo?${consulta}`).then(setEst).catch(() => setEst(null));
  }, [consulta, municipio]);
  useEffect(carregar, [carregar]);

  const salvarECalcular = async (e: FormEvent) => {
    e.preventDefault();
    if (!param) return;
    try {
      await api("/v1/edificacoes/consumo/parametros", { metodo: "PUT", corpo: { empresa, municipio, parametros: param, motivo } });
      const r = await api<Estimativa>(`/v1/edificacoes/consumo/calcular?${consulta}`, { metodo: "POST" });
      setEst(r);
      setAviso(`Estimativa calculada: ${n(r.total_estimado_l_s, 3)} l/s (modelo ${n(r.total_modelo_l_s, 3)} l/s). Candidato — o modelo oficial não muda.`);
    } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };

  return (
    <section className="nx-cartao" aria-label="Consumo estimado por nó">
      <h3>Consumo estimado por nó (candidato)</h3>
      <p className="nx-suave">Estimativa feita com as edificações importadas e os parâmetros da engenharia, para comparar com as demandas do modelo
        EPANET e com o medido. Nunca altera o modelo oficial.</p>
      {aviso && <p className="nx-suave" role="status">{aviso}</p>}
      {est ? (
        <>
          <p className="nx-suave">Calculado em {new Date(est.criado_em).toLocaleString("pt-BR")} por {est.criado_por} · per capita {n(est.per_capita_l_hab_dia, 1)} l/hab/dia ({est.per_capita_origem})
            {est.edificacoes_sem_no ? ` · ${est.edificacoes_sem_no} edificação(ões) sem nó da rede` : ""}</p>
          <div className="tabela-rolagem">
            <table className="tabela-composicao" aria-label="Consumo por setor">
              <thead><tr><th>Setor</th><th>Nós</th><th>Edificações</th><th>Estimado (l/s)</th><th>Modelo (l/s)</th><th>Medido (l/s)</th><th>Estimado ÷ modelo</th><th>Estimado ÷ medido</th></tr></thead>
              <tbody>{est.setores.map((s) => (
                <tr key={s.setor}><td>{s.setor}</td><td className="nx-valor">{s.nos}</td><td className="nx-valor">{s.edificacoes}</td>
                  <td className="nx-valor">{n(s.estimado_l_s, 3)}</td><td className="nx-valor">{n(s.modelo_l_s, 3)}</td>
                  <td className="nx-valor">{s.medido_l_s === null ? "sem medição" : n(s.medido_l_s, 3)}</td>
                  <td className="nx-valor">{n(s.estimado_sobre_modelo)}</td><td className="nx-valor">{n(s.estimado_sobre_medido)}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <details><summary>Limitações</summary><ul>{est.limitacoes.map((l) => <li key={l}><small>{l}</small></li>)}</ul></details>
          <button type="button" className="nx-botao" onClick={() => void baixar(`/v1/edificacoes/consumo.csv?${consulta}`, `consumo_estimado_${municipio}.csv`)}>Baixar CSV por nó</button>
        </>
      ) : <p className="nx-suave">Nenhuma estimativa calculada para este município.</p>}
      {podeEditar && param && (
        <form className="form-filial" aria-label="Parâmetros do consumo estimado" onSubmit={(e) => void salvarECalcular(e)}>
          {CAMPOS.map(([chave, rotulo]) => (
            <div className="nx-campo" key={chave}><label htmlFor={`ce-${chave}`}>{rotulo}</label>
              <input id={`ce-${chave}`} inputMode="decimal" value={param[chave] === null ? "" : String(param[chave])}
                onChange={(e) => setParam({ ...param, [chave]: e.target.value.trim() === "" ? null : Number(e.target.value.replace(",", ".")) })} /></div>
          ))}
          <label><input type="checkbox" checked={param.desconhecido_como_residencial}
            onChange={(e) => setParam({ ...param, desconhecido_como_residencial: e.target.checked })} /> uso desconhecido conta como residencial</label>
          <div className="nx-campo"><label htmlFor="ce-motivo">Motivo</label>
            <input id="ce-motivo" required minLength={5} maxLength={300} value={motivo} placeholder="ex.: pesquisa de campo 2026"
              onChange={(e) => setMotivo(e.target.value)} /></div>
          <button type="submit" className="nx-botao">Salvar e calcular</button>
        </form>
      )}
    </section>
  );
}
